import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { graphqlApi } from '../../../services/graphql';
import { useAuthStore } from '../../../store/authStore';
import type { Influencer } from '../../../types';

import { INTEREST_CATEGORIES, ACTIVITY_TYPES, EQUIPMENT_TYPES, MARITAL_STATUS, CHILDREN_STATUS } from '../../../constants/categories';

const SNS_URL_PREFIXES: Record<string, string> = {
  instagram: 'https://www.instagram.com/',
  naverblog: 'https://blog.naver.com/',
  youtube: 'https://www.youtube.com/',
};

function extractSnsId(key: string, value: string): string {
  const prefix = SNS_URL_PREFIXES[key];
  if (prefix && value.startsWith(prefix)) return value.slice(prefix.length);
  return value;
}

function buildSnsUrl(key: string, id: string): string {
  if (!id) return '';
  const prefix = SNS_URL_PREFIXES[key];
  return prefix ? `${prefix}${id}` : id;
}

const SNS_CHANNELS = [
  {
    key: 'instagram' as const,
    label: '인스타그램',
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
        <rect x="2" y="2" width="20" height="20" rx="5" stroke="url(#igP)" strokeWidth="2"/>
        <circle cx="12" cy="12" r="5" stroke="url(#igP)" strokeWidth="2"/>
        <circle cx="17.5" cy="6.5" r="1.5" fill="url(#igP)"/>
        <defs><linearGradient id="igP" x1="2" y1="22" x2="22" y2="2"><stop stopColor="#FFDC80"/><stop offset=".5" stopColor="#F56040"/><stop offset="1" stopColor="#833AB4"/></linearGradient></defs>
      </svg>
    ),
  },
  {
    key: 'naverblog' as const,
    label: '블로그',
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
        <path d="M16.273 12.845 7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727v12.845Z" fill="#03C75A"/>
      </svg>
    ),
  },
  {
    key: 'youtube' as const,
    label: '유튜브',
    icon: (
      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814ZM9.545 15.568V8.432L15.818 12l-6.273 3.568Z" fill="#FF0000"/>
      </svg>
    ),
  },
];

interface ProfileFormState {
  snsChannels: {
    instagram: { account: string; followerCount: number };
    youtube: { account: string; followerCount: number };
    tiktok: { account: string; followerCount: number };
    twitter: { account: string; followerCount: number };
    naverblog: { account: string; followerCount: number };
  };
  categories: string[];
  mainCategory: string;
  activityType: string;
  equipment: string[];
  maritalStatus: string;
  hasChildren: string;
  portfolio: string[];
  ratePerPost: number;
  engagementRate: number;
  bio: string;
  location: string;
  languages: string[];
}

function mapInfluencerToFormState(influencer: Influencer): ProfileFormState {
  const ch = influencer.socialMediaHandles || {};
  return {
    snsChannels: {
      instagram: { account: extractSnsId('instagram', ch.instagram?.account ?? ''), followerCount: ch.instagram?.followerCount ?? 0 },
      youtube: { account: extractSnsId('youtube', ch.youtube?.account ?? ''), followerCount: ch.youtube?.followerCount ?? 0 },
      tiktok: { account: ch.tiktok?.account ?? '', followerCount: ch.tiktok?.followerCount ?? 0 },
      twitter: { account: ch.twitter?.account ?? '', followerCount: ch.twitter?.followerCount ?? 0 },
      naverblog: { account: extractSnsId('naverblog', ch.naverblog?.account ?? ''), followerCount: ch.naverblog?.followerCount ?? 0 },
    },
    categories: influencer.categories ?? [],
    mainCategory: influencer.mainCategory ?? '',
    activityType: (influencer as any).activityType ?? '',
    equipment: (influencer as any).equipment ?? [],
    maritalStatus: (influencer as any).maritalStatus ?? '',
    hasChildren: (influencer as any).hasChildren ?? '',
    portfolio: influencer.portfolio ?? [],
    ratePerPost: influencer.ratePerPost ?? 0,
    engagementRate: influencer.engagementRate ?? 0,
    bio: influencer.bio ?? '',
    location: influencer.location ?? '',
    languages: influencer.languages ?? [],
  };
}

const SOCIAL_PLATFORMS = ['instagram', 'youtube', 'tiktok', 'twitter', 'naverblog'] as const;
type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

const ProfileSubPage: React.FC = () => {
  const { t } = useTranslation('profile');
  const { user } = useAuthStore();

  const [influencer, setInfluencer] = useState<Influencer | null>(null);
  const [formState, setFormState] = useState<ProfileFormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingChannel, setSavingChannel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newPortfolioUrl, setNewPortfolioUrl] = useState('');

  const fetchInfluencer = useCallback(async () => {
    if (!user?.userId) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await graphqlApi.getInfluencerByUserId(user.userId);
      if (response.success && response.data) {
        setInfluencer(response.data);
        setFormState(mapInfluencerToFormState(response.data));
      } else {
        setError(response.error || t('myProfile.profileSubPage.loadFailed'));
      }
    } catch (err: any) {
      console.error('Error fetching influencer profile:', err);
      setError(err.message || t('myProfile.profileSubPage.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [user?.userId, t]);

  useEffect(() => { fetchInfluencer(); }, [fetchInfluencer]);

  const updateSocialHandle = (platform: SocialPlatform, value: string) => {
    setFormState((prev) => prev ? { ...prev, snsChannels: { ...prev.snsChannels, [platform]: { ...prev.snsChannels[platform], account: value } } } : prev);
  };

  const updateFollowerCount = (platform: SocialPlatform, value: number) => {
    setFormState((prev) => prev ? { ...prev, snsChannels: { ...prev.snsChannels, [platform]: { ...prev.snsChannels[platform], followerCount: value } } } : prev);
  };

  const toggleCategory = (category: string) => {
    setFormState((prev) => {
      if (!prev) return prev;
      const exists = prev.categories.includes(category);
      return { ...prev, categories: exists ? prev.categories.filter((c) => c !== category) : [...prev.categories, category] };
    });
  };

  const addPortfolioLink = () => {
    const trimmed = newPortfolioUrl.trim();
    if (!trimmed || !formState) return;
    setFormState({ ...formState, portfolio: [...formState.portfolio, trimmed] });
    setNewPortfolioUrl('');
  };

  const removePortfolioLink = (index: number) => {
    setFormState((prev) => prev ? { ...prev, portfolio: prev.portfolio.filter((_, i) => i !== index) } : prev);
  };

  const handleSaveChannel = async (channelKey: string) => {
    if (!influencer?.influencerId || !formState) return;
    setSavingChannel(channelKey);
    try {
      const ch = formState.snsChannels[channelKey as keyof typeof formState.snsChannels];
      const fullUrl = buildSnsUrl(channelKey, (ch?.account || '').trim());
      const followerCount = ch?.followerCount || 0;
      const updatedHandles: Record<string, { account?: string; followerCount?: number }> = {};
      const existingHandles = influencer.socialMediaHandles || {};
      for (const platform of ['instagram', 'twitter', 'tiktok', 'youtube', 'naverblog']) {
        const existing = (existingHandles as any)[platform];
        if (existing?.account || existing?.followerCount) {
          updatedHandles[platform] = { account: existing.account, followerCount: existing.followerCount };
        }
      }
      updatedHandles[channelKey] = { account: fullUrl, followerCount };
      const res = await graphqlApi.updateInfluencer(influencer.influencerId, {
        socialMediaHandles: updatedHandles,
      });
      if (res.success) {
        setInfluencer(prev => prev ? { ...prev, socialMediaHandles: updatedHandles } : prev);
        toast.success(`${SNS_CHANNELS.find(c => c.key === channelKey)?.label || channelKey} 저장 완료`);
      } else {
        toast.error(res.error || '저장에 실패했습니다');
      }
    } catch (err: any) {
      toast.error(err.message || '저장에 실패했습니다');
    } finally {
      setSavingChannel(null);
    }
  };

  const handleSave = async () => {
    if (!influencer?.influencerId || !formState) return;
    try {
      setSaving(true);
      const response = await graphqlApi.updateInfluencer(influencer.influencerId, {
        socialMediaHandles: Object.fromEntries(
          Object.entries(formState.snsChannels)
            .filter(([, v]) => v.account || v.followerCount)
            .map(([k, v]) => [k, { account: buildSnsUrl(k, (v.account || '').trim()), followerCount: v.followerCount }])
        ),
        categories: formState.categories,
        mainCategory: formState.mainCategory,
        activityType: formState.activityType,
        equipment: formState.equipment,
        maritalStatus: formState.maritalStatus,
        hasChildren: formState.hasChildren,
        portfolio: formState.portfolio,
        ratePerPost: formState.ratePerPost,
        engagementRate: formState.engagementRate,
        bio: formState.bio,
        location: formState.location,
        languages: formState.languages,
      });
      if (response.success) {
        toast.success(t('myProfile.profileSubPage.saveSuccess'));
      } else {
        toast.error(response.error || t('myProfile.profileSubPage.saveFailed'));
      }
    } catch (err: any) {
      console.error('Error saving profile:', err);
      toast.error(t('myProfile.profileSubPage.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-2 text-gray-600">{t('common.loading', 'Loading...')}</span>
      </div>
    );
  }

  if (error || !formState) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-red-600 mb-4">{error || t('myProfile.profileSubPage.loadFailed')}</p>
        <button onClick={fetchInfluencer} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors">
          {t('myProfile.profileSubPage.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-8">
      {/* Page Title */}
      <div className="pt-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">{t('myProfile.profileSubPage.title')}</h2>
        <hr className="border-gray-200" />
      </div>

      {/* SNS 채널정보 */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">SNS 채널정보</h2>
          <p className="text-sm text-gray-500 mt-1">
            정확하지 않은 팔로워 수의 경우 관리자에 의해 수정될 수 있습니다.
          </p>
        </div>
        <hr className="border-gray-200" />
        <div className="space-y-4">
          {SNS_CHANNELS.map(({ key, label, icon }) => {
            const ch = formState.snsChannels[key];
            const account = ch?.account || '';
            const followers = ch?.followerCount || 0;
            const isSaving = savingChannel === key;
            return (
              <div key={key} className="border border-gray-200 rounded-lg p-5 bg-white space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex-shrink-0">{icon}</div>
                  <span className="text-sm font-semibold text-gray-800">{label}</span>
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">계정 아이디</label>
                    <input
                      type="text"
                      value={account}
                      onChange={(e) => updateSocialHandle(key, e.target.value)}
                      placeholder={key === 'youtube' ? '@channel_id' : 'account_id'}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">팔로워 수</label>
                    <input
                      type="number"
                      min={0}
                      value={followers || ''}
                      onChange={(e) => updateFollowerCount(key, parseInt(e.target.value) || 0)}
                      placeholder="0"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Profile Section: Bio + Areas of Interest */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">{t('myProfile.profileSubPage.profileSectionTitle')}</h2>
          <p className="text-sm text-gray-400 mt-1">{t('myProfile.profileSubPage.profileSectionDesc')}</p>
        </div>
        <hr className="border-gray-200" />
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">{t('myProfile.profileSubPage.bio')}</label>
          <textarea
            value={formState.bio}
            onChange={(e) => setFormState({ ...formState, bio: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t('myProfile.profileSubPage.bioPlaceholder')}
          />
        </div>
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">{t('myProfile.profileSubPage.categories')}</label>
          <div className="flex flex-wrap gap-2">
            {INTEREST_CATEGORIES.map((cat) => (
              <button key={cat} type="button" onClick={() => toggleCategory(cat)}
                className={`px-3 py-1.5 text-sm rounded-lg border-2 transition-colors ${formState.categories.includes(cat) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                {t(`common:categories.${cat}`)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Activity Info */}
      <section>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">{t('common:activityTypes.title')}</h2>
        <hr className="border-gray-200 mb-4" />
        <label className="block text-sm font-bold text-gray-700 mb-2">{t('common:activityTypes.activityType')}</label>
        <div className="flex flex-wrap gap-2">
          {ACTIVITY_TYPES.map((type) => (
            <button key={type} type="button" onClick={() => setFormState({ ...formState, activityType: type })}
              className={`px-4 py-2 text-sm font-medium rounded-lg border-2 transition-colors ${formState.activityType === type ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
              {t(`common:activityTypes.${type}`)}
            </button>
          ))}
        </div>
      </section>

      {/* Filming Equipment */}
      <section>
        <label className="block text-sm font-bold text-gray-700 mb-2">{t('common:equipmentTypes.title')}</label>
        <div className="flex flex-wrap gap-2">
          {EQUIPMENT_TYPES.map((eq) => {
            const selected = formState.equipment.includes(eq);
            return (
              <button key={eq} type="button"
                onClick={() => setFormState((prev) => prev ? { ...prev, equipment: selected ? prev.equipment.filter((e) => e !== eq) : [...prev.equipment, eq] } : prev)}
                className={`px-4 py-2 text-sm font-medium rounded-lg border-2 transition-colors ${selected ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                {t(`common:equipmentTypes.${eq}`)}
              </button>
            );
          })}
        </div>
      </section>

      {/* Life Info */}
      <section>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">{t('common:lifeInfo.title')}</h2>
        <hr className="border-gray-200 mb-4" />
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">{t('common:lifeInfo.maritalStatus')}</label>
            <div className="flex flex-wrap gap-2">
              {MARITAL_STATUS.map((status) => (
                <button key={status} type="button" onClick={() => setFormState({ ...formState, maritalStatus: status })}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border-2 transition-colors ${formState.maritalStatus === status ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                  {t(`common:lifeInfo.${status}`)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">{t('common:lifeInfo.children')}</label>
            <div className="flex flex-wrap gap-2">
              {CHILDREN_STATUS.map((status) => (
                <button key={status} type="button" onClick={() => setFormState({ ...formState, hasChildren: status })}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border-2 transition-colors ${formState.hasChildren === status ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                  {t(`common:lifeInfo.${status}`)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Save Button */}
      <div className="pt-4 border-t border-gray-200">
        <button onClick={handleSave} disabled={saving}
          className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? t('myProfile.profileSubPage.saving') : t('myProfile.profileSubPage.save')}
        </button>
      </div>
    </div>
  );
};

export default ProfileSubPage;
